import { useState, useCallback, useRef } from 'react';

const MAX_HISTORY = 50;

export function useCanvasHistory(elements, setElements) {
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Push current state to history
  const pushHistory = useCallback(() => {
    // Clone current elements
    const snapshot = JSON.parse(JSON.stringify(elements));
    
    // Remove any redo history
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    
    // Add new snapshot
    historyRef.current.push(snapshot);
    historyIndexRef.current = historyRef.current.length - 1;
    
    // Limit history size
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
      historyIndexRef.current--;
    }
    
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, [elements]);

  // Undo to previous state
  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    
    historyIndexRef.current--;
    const snapshot = historyRef.current[historyIndexRef.current];
    
    if (snapshot) {
      setElements(JSON.parse(JSON.stringify(snapshot)));
    }
    
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  }, [setElements]);

  // Redo to next state
  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    
    historyIndexRef.current++;
    const snapshot = historyRef.current[historyIndexRef.current];
    
    if (snapshot) {
      setElements(JSON.parse(JSON.stringify(snapshot)));
    }
    
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  }, [setElements]);

  // Initialize history with current state
  const initHistory = useCallback(() => {
    historyRef.current = [JSON.parse(JSON.stringify(elements))];
    historyIndexRef.current = 0;
    setCanUndo(false);
    setCanRedo(false);
  }, [elements]);

  return {
    pushHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    initHistory
  };
}

export default useCanvasHistory;
